import api from './api';

/**
 * Membership Management API
 * Handling all membership, loyalty, and subscription operations
 */

// Base path for all membership API calls
const BASE = '/membership';

// --- Member Self-Service (Portal) ---
const membershipApi = {
  auth: {
    login: (credentials) => api.post(`${BASE}/auth/login`, credentials),
    register: (memberData) => api.post(`${BASE}/auth/register`, memberData),
    getProfile: () => api.get(`${BASE}/profile`),
  },
};

// --- Member Management (Admin/Staff) ---
export const memberAPI = {
  getAll: (params) => api.get(`${BASE}/members`, { params }),
  getById: (id) => api.get(`${BASE}/members/${id}`),
  create: (memberData) => api.post(`${BASE}/members`, memberData),
  update: (id, memberData) => api.put(`${BASE}/members/${id}`, memberData),
  delete: (id) => api.delete(`${BASE}/members/${id}`),
  importLegacyMembers: (data) => api.post(`${BASE}/members/legacy-import`, data),
  bulkValidate: (rows) => api.post(`${BASE}/members/bulk-validate`, rows),
  bulkImport: (rows) => api.post(`${BASE}/members/bulk`, rows),
  requestRemoval: (id, notes) => api.post(`${BASE}/members/${id}/request-removal`, { notes }),
  addPoints: (id, points) => api.post(`${BASE}/members/${id}/points/add`, { pointsToCredit: points }),
  getUpgradePreview: (id, typeId) => api.get(`${BASE}/members/${id}/upgrade-preview`, { params: { typeId } }),
};

// --- Membership Types & Tiers ---
export const membershipTypeAPI = {
  getAll: (params) => api.get(`${BASE}/types`, { params }),
  getById: (id) => api.get(`${BASE}/types/${id}`),
  create: (typeData) => api.post(`${BASE}/types`, typeData),
  update: (id, typeData) => api.put(`${BASE}/types/${id}`, typeData),
  delete: (id) => api.delete(`${BASE}/types/${id}`),
};

// --- Required document types (Membership Types > Manage Documents) ---
export const documentTypeAPI = {
  getAll: (params) => api.get(`${BASE}/document-types`, { params }),
  create: (data) => api.post(`${BASE}/document-types`, data),
  update: (id, data) => api.put(`${BASE}/document-types/${id}`, data),
  delete: (id) => api.delete(`${BASE}/document-types/${id}`),
};

// --- Applications ---
export const memberApplicationAPI = {
  getAll: (params) => api.get(`${BASE}/applications`, { params }),
  getById: (id) => api.get(`${BASE}/applications/${id}`),
  create: (applicationData) => api.post(`${BASE}/applications`, applicationData),
  bulkCreate: (data) => api.post(`${BASE}/applications/bulk`, data),
  validateBulk: (data) => api.post(`${BASE}/applications/validate-bulk`, data),
  deleteBatch: (batchId) => api.delete(`${BASE}/applications/batch/${batchId}`),
  deleteBulk: (ids) => api.post(`${BASE}/applications/bulk-delete`, { ids }),
  update: (id, applicationData) => api.put(`${BASE}/applications/${id}`, applicationData),
  delete: (id) => api.delete(`${BASE}/applications/${id}`),
  upload: (formData) => api.post(`${BASE}/applications/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  sendApplicationLink: (data) => api.post(`${BASE}/applications/send-link`, data),
  resendApplicationLink: (id, data) => api.post(`${BASE}/applications/${id}/resend-link`, data),
  getPublicApplicationForm: (token) => api.get(`${BASE}/applications/public/form/${token}`),
  submitPublicApplicationForm: (token, data) => api.post(`${BASE}/applications/public/form/${token}/submit`, data),
  generateDocumentLink: (id) => api.post(`${BASE}/applications/${id}/generate-document-link`),
  getPublicApplicationByToken: (token) => api.get(`${BASE}/applications/public/documents/${token}`),
  uploadPublicDocument: (token, formData) => api.post(`${BASE}/applications/public/documents/${token}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deletePublicDocument: (token, name) => api.delete(`${BASE}/applications/public/documents/${token}/files/${name}`),
  submitPublicDocuments: (token) => api.post(`${BASE}/applications/public/documents/${token}/submit`),
  logCustomAction: (id, action, notes) => api.post(`${BASE}/applications/${id}/activity-log`, { action, notes }),
};

// --- Approvals ---
export const approvalAPI = {
  getAll: (params) => api.get(`${BASE}/approvals`, { params }),
  getPending: (params) => api.get(`${BASE}/approvals`, { params: { ...params, status: 'pending,pending_committee,payment_pending' } }),
  getById: (id) => api.get(`${BASE}/approvals/${id}`),
  approveMembership: (id, data) => api.post(`${BASE}/approvals/${id}/approve-membership`, data),
  approveCommittee: (id, data) => api.post(`${BASE}/approvals/${id}/approve-committee`, data),
  requestInfo: (id, data) => api.post(`${BASE}/approvals/${id}/request-info`, data),
  reject: (id, data) => api.post(`${BASE}/approvals/${id}/reject`, data),
};


// --- Invoices ---
export const membershipInvoiceAPI = {
  getAll: (params) => api.get(`${BASE}/invoices`, { params }),
  getById: (id) => api.get(`${BASE}/invoices/${id}`),
  // New members' invoices still at the full subscription though paid in the discount window
  getNewMemberDiscountCandidates: () => api.get(`${BASE}/invoices/new-member-discount`),
  applyNewMemberDiscount: (invoiceIds) => api.post(`${BASE}/invoices/new-member-discount/apply`, { invoiceIds }),
  getNewMemberDiscountAuto: () => api.get(`${BASE}/invoices/new-member-discount/auto`),
  setNewMemberDiscountAuto: (enabled) => api.put(`${BASE}/invoices/new-member-discount/auto`, { enabled }),
  // Lifetime invoices carrying the new member discount by mistake; take it off one invoice
  getDiscountedLifetimeInvoices: () => api.get(`${BASE}/invoices/new-member-discount/lifetime`),
  removeNewMemberDiscount: (id, reason) => api.post(`${BASE}/invoices/${id}/remove-new-member-discount`, { reason }),
  excludeFromNewMemberDiscount: (invoiceIds, reason) => api.post(`${BASE}/invoices/new-member-discount/exclude`, { invoiceIds, reason }),
  getNewMemberDiscountExcluded: () => api.get(`${BASE}/invoices/new-member-discount/excluded`),
  includeInNewMemberDiscount: (invoiceIds) => api.post(`${BASE}/invoices/new-member-discount/include`, { invoiceIds }),
  clearAll: () => api.delete(`${BASE}/invoices/clear-all`),
  create: (invoiceData) => api.post(`${BASE}/invoices`, invoiceData),
  update: (id, invoiceData) => api.put(`${BASE}/invoices/${id}`, invoiceData),
  delete: (id) => api.delete(`${BASE}/invoices/${id}`),
  sendEmail: (id) => api.post(`${BASE}/invoices/${id}/send`),
  generatePaymentLink: (id, data) => api.post(`${BASE}/invoices/${id}/generate-payment-link`, data),
  sendPaymentLinkSms: (id, data) => api.post(`${BASE}/invoices/${id}/send-sms`, data),
  sendPaymentLinkEmail: (id, data) => api.post(`${BASE}/invoices/${id}/send-email-link`, data),
  getPublicByToken: (token) => api.get(`${BASE}/invoices/public/${token}`),
  submitPublicBankTransfer: (token, formData) => api.post(`${BASE}/invoices/public/${token}/bank-transfer`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  submitPublicPayment: (token, data) => api.post(`${BASE}/invoices/public/${token}/submit`, data),
  print: (id, variant) => api.get(`${BASE}/invoices/${id}/print`, { responseType: 'blob', params: variant ? { variant } : undefined }),
  downloadProforma: (id) => api.get(`${BASE}/invoices/${id}/proforma`, { responseType: 'blob' }),
  downloadPublicProforma: (token) => api.get(`${BASE}/invoices/public/${token}/proforma`, { responseType: 'blob' }),
};

// --- Payments ---
export const membershipPaymentAPI = {
  getAll: (params) => api.get(`${BASE}/payments`, { params }),
  getById: (id) => api.get(`${BASE}/payments/${id}`),
  create: (data) => api.post(`${BASE}/payments`, data),
  approve: (id) => api.post(`${BASE}/payments/${id}/approve`),
  // Missing / unreadable slip: ask the member again (email/SMS link), or staff attach it
  requestReceiptReupload: (id, data) => api.post(`${BASE}/payments/${id}/request-reupload`, data),
  uploadReceipt: (id, formData) => api.post(`${BASE}/payments/${id}/receipt`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  addReceipts: (id, formData) => api.post(`${BASE}/payments/${id}/receipts`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  removeAddedReceipt: (id, slipId) => api.delete(`${BASE}/payments/${id}/receipts/${slipId}`),
  getReuploadByToken: (token) => api.get(`${BASE}/payments/public/reupload/${token}`),
  submitReupload: (token, formData) => api.post(`${BASE}/payments/public/reupload/${token}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  reject: (id, reason) => api.post(`${BASE}/payments/${id}/reject`, { reason }),
  update: (id, data) => api.put(`${BASE}/payments/${id}`, data),
  printReceipt: (id, variant) => api.get(`${BASE}/payments/${id}/receipt/print`, { responseType: 'blob', params: variant ? { variant } : undefined }),
  emailReceipt: (id, data) => api.post(`${BASE}/payments/${id}/receipt/email`, data),
  delete: (id) => api.delete(`${BASE}/payments/${id}`),
  initiateCard: (data) => api.post(`${BASE}/paycorp/initiate`, data),
  initiatePublicCard: (token, data) => api.post(`${BASE}/paycorp/initiate-public/${token}`, data),
  clearAll: () => api.delete(`${BASE}/payments/clear-all`),
  // Helper: build a receipt URL that goes through the API gateway (avoids direct port access)
  getReceiptUrl: (receiptUrl) => {
    if (!receiptUrl) return null;
    const filename = receiptUrl.split('/').pop();
    const apiBase = import.meta.env.VITE_APP_API_URL || 'http://localhost:3001/api';
    return `${apiBase}${BASE}/invoices/receipt/${filename}`;
  },
};

// --- Renewals ---
export const renewalAPI = {
  getAll: (params) => api.get(`${BASE}/renewals`, { params }),
  getExpiring: (params) => api.get(`${BASE}/renewals/expiring`, { params }),
  getById: (id) => api.get(`${BASE}/renewals/${id}`),
  create: (renewalData) => api.post(`${BASE}/renewals`, renewalData),
  process: (id, data) => api.post(`${BASE}/renewals/${id}/process`, data),
  generateInvoice: (memberId) => api.post(`${BASE}/renewals/${memberId}/generate-invoice`),
  getAutoTemplate: () => api.get(`${BASE}/renewals/reminders/auto-template`),
  updateAutoTemplate: (data) => api.put(`${BASE}/renewals/reminders/auto-template`, data),
  runAutoSendNow: () => api.post(`${BASE}/renewals/reminders/auto-template/run-now`),
  getReminderHistory: (params) => api.get(`${BASE}/renewals/reminders/history`, { params }),
  sendReminders: (data) => api.post(`${BASE}/renewals/reminders/send`, data),
  getStatusPolicy: () => api.get(`${BASE}/renewals/reminders/status-policy`),
  updateStatusPolicy: (data) => api.put(`${BASE}/renewals/reminders/status-policy`, data),
  runStatusPolicyNow: () => api.post(`${BASE}/renewals/reminders/status-policy/run-now`),
  getRenewalInvoices: (params) => api.get(`${BASE}/renewals/invoices`, { params }),
  generateRenewalInvoices: (data) => api.post(`${BASE}/renewals/invoices/generate`, data),
  sendRenewalInvoice: (id, data) => api.post(`${BASE}/renewals/invoices/${id}/send`, data),
  downloadRenewalInvoiceProforma: (id) => api.get(`${BASE}/renewals/invoices/${id}/proforma`, { responseType: 'blob' }),
  sendRenewalInvoicesBulk: (data) => api.post(`${BASE}/renewals/invoices/send-bulk`, data),
  getInvoiceAutoSettings: () => api.get(`${BASE}/renewals/invoices/auto-settings`),
  updateInvoiceAutoSettings: (data) => api.put(`${BASE}/renewals/invoices/auto-settings`, data),
};


// --- Birthday Wishes ---
export const birthdayWishAPI = {
  getUpcoming: (params) => api.get(`${BASE}/birthday-wishes/upcoming`, { params }),
  getTemplate: () => api.get(`${BASE}/birthday-wishes/template`),
  getHistory: (params) => api.get(`${BASE}/birthday-wishes`, { params }),
  send: (data) => api.post(`${BASE}/birthday-wishes/send`, data),
  sendBulk: (data) => api.post(`${BASE}/birthday-wishes/send-bulk`, data),
  getAutoTemplate: () => api.get(`${BASE}/birthday-wishes/auto-template`),
  updateAutoTemplate: (data) => api.put(`${BASE}/birthday-wishes/auto-template`, data),
  runAutoSendNow: () => api.post(`${BASE}/birthday-wishes/auto-template/run-now`),
};

// --- Events ---
export const membershipEventAPI = {
  getAll: (params) => api.get(`${BASE}/events`, { params }),
  getById: (id) => api.get(`${BASE}/events/${id}`),
  create: (eventData) => api.post(`${BASE}/events`, eventData),
  update: (id, eventData) => api.put(`${BASE}/events/${id}`, eventData),
  delete: (id) => api.delete(`${BASE}/events/${id}`),
  register: (id, data) => api.post(`${BASE}/events/${id}/register`, data),
  sendEmail: (id, formData) => api.post(`${BASE}/events/${id}/send-email`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  sendSms: (id, data) => api.post(`${BASE}/events/${id}/send-sms`, data),
  getNotices: (id) => api.get(`${BASE}/events/${id}/notices`),
  getNoticeProgress: (id, noticeId, params) => api.get(`${BASE}/events/${id}/notices/${noticeId}`, { params }),
};
// --- Event Ticketing (ticket templates, per-event allocations, issuing, QR check-in) ---
export const ticketAPI = {
  listTemplates: (params) => api.get(`${BASE}/ticket-templates`, { params }),
  createTemplate: (data) => api.post(`${BASE}/ticket-templates`, data),
  updateTemplate: (templateId, data) => api.put(`${BASE}/ticket-templates/${templateId}`, data),
  deleteTemplate: (templateId) => api.delete(`${BASE}/ticket-templates/${templateId}`),
  sampleQr: () => api.get(`${BASE}/ticket-templates/sample-qr`),
  getTicketing: (eventId) => api.get(`${BASE}/events/${eventId}/ticketing`),
  updateTicketing: (eventId, data) => api.put(`${BASE}/events/${eventId}/ticketing`, data),
  listTickets: (eventId, params) => api.get(`${BASE}/events/${eventId}/tickets`, { params }),
  getTicket: (eventId, ticketId) => api.get(`${BASE}/events/${eventId}/tickets/${ticketId}`),
  sendTickets: (eventId, data) => api.post(`${BASE}/events/${eventId}/tickets/send`, data),
  resendTicket: (eventId, ticketId) => api.post(`${BASE}/events/${eventId}/tickets/${ticketId}/resend`),
  cancelTicket: (eventId, ticketId, reason) => api.post(`${BASE}/events/${eventId}/tickets/${ticketId}/cancel`, { reason }),
  checkIn: (eventId, code) => api.post(`${BASE}/events/${eventId}/tickets/check-in`, { code }),
  undoCheckIn: (eventId, ticketId) => api.post(`${BASE}/events/${eventId}/tickets/${ticketId}/undo-check-in`),
  // Ticket sales link (staff)
  getSales: (eventId) => api.get(`${BASE}/events/${eventId}/ticket-sales`),
  updateSales: (eventId, data) => api.put(`${BASE}/events/${eventId}/ticket-sales`, data),
  newSalesLink: (eventId) => api.post(`${BASE}/events/${eventId}/ticket-sales/new-link`),
};

// --- Public ticket sales page + the buyer's order page (no login) ---
export const publicTicketSalesAPI = {
  getPage: (token) => api.get(`${BASE}/ticket-sales/public/${token}`),
  lookup: (token, data) => api.post(`${BASE}/ticket-sales/public/${token}/lookup`, data),
  purchase: (token, data) => api.post(`${BASE}/ticket-sales/public/${token}/purchase`, data),
  getOrder: (orderToken) => api.get(`${BASE}/ticket-sales/public/orders/${orderToken}`),
  setPaymentMethod: (orderToken, method) => api.post(`${BASE}/ticket-sales/public/orders/${orderToken}/payment-method`, { method }),
};

// --- Practicing Marketer Applications ---
export const pmApplicationAPI = {
  getAll: (params) => api.get(`${BASE}/pm-applications`, { params }),
  sendLinks: (data) => api.post(`${BASE}/pm-applications/send-links`, data),
  renewLinks: (data) => api.post(`${BASE}/pm-applications/renew-links`, data),
  createForMember: (data) => api.post(`${BASE}/pm-applications/create-for-member`, data),
  approve: (id, data) => api.post(`${BASE}/pm-applications/${id}/approve`, data),
  reject: (id, data) => api.post(`${BASE}/pm-applications/${id}/reject`, data),
  generatePayment: (id, data) => api.post(`${BASE}/pm-applications/${id}/generate-payment`, data),
  remove: (id, data) => api.post(`${BASE}/pm-applications/${id}/remove`, data),
  
  // Public endpoints
  getPublic: (token) => api.get(`${BASE}/pm-applications/public/${token}`),
  submitPublic: (token, formData) => api.post(`${BASE}/pm-applications/public/${token}/submit`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
};

// --- Facilities ---
export const facilityAPI = {
  getAll: (params) => api.get(`${BASE}/facilities`, { params }),
  getById: (id) => api.get(`${BASE}/facilities/${id}`),
  create: (facilityData) => api.post(`${BASE}/facilities`, facilityData),
  update: (id, facilityData) => api.put(`${BASE}/facilities/${id}`, facilityData),
  delete: (id) => api.delete(`${BASE}/facilities/${id}`),
  sendEmail: (id, formData) => api.post(`${BASE}/facilities/${id}/send-email`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  sendSms: (id, data) => api.post(`${BASE}/facilities/${id}/send-sms`, data),
  getNotices: (id) => api.get(`${BASE}/facilities/${id}/notices`),
  getNoticeProgress: (id, noticeId, params) => api.get(`${BASE}/facilities/${id}/notices/${noticeId}`, { params }),
};

export const facilityTypeAPI = {
  getAll: (params) => api.get(`${BASE}/facility-types`, { params }),
  getById: (id) => api.get(`${BASE}/facility-types/${id}`),
  create: (data) => api.post(`${BASE}/facility-types`, data),
  update: (id, data) => api.put(`${BASE}/facility-types/${id}`, data),
  delete: (id) => api.delete(`${BASE}/facility-types/${id}`),
};

// --- Benefits ---
export const benefitAPI = {
  getAll: (params) => api.get(`${BASE}/benefits`, { params }),
  getById: (id) => api.get(`${BASE}/benefits/${id}`),
  create: (benefitData) => api.post(`${BASE}/benefits`, benefitData),
  update: (id, benefitData) => api.put(`${BASE}/benefits/${id}`, benefitData),
  delete: (id) => api.delete(`${BASE}/benefits/${id}`),
  sendEmail: (id, formData) => api.post(`${BASE}/benefits/${id}/send-email`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  sendSms: (id, data) => api.post(`${BASE}/benefits/${id}/send-sms`, data),
  getNotices: (id) => api.get(`${BASE}/benefits/${id}/notices`),
  getNoticeProgress: (id, noticeId, params) => api.get(`${BASE}/benefits/${id}/notices/${noticeId}`, { params }),
};

export const benefitCategoryAPI = {
  getAll: (params) => api.get(`${BASE}/benefit-categories`, { params }),
  create: (data) => api.post(`${BASE}/benefit-categories`, data),
  update: (id, data) => api.put(`${BASE}/benefit-categories/${id}`, data),
  delete: (id) => api.delete(`${BASE}/benefit-categories/${id}`),
};

// --- Add-ons ---
export const addonAPI = {
  getAll: (params) => api.get(`${BASE}/addons`, { params }),
  getById: (id) => api.get(`${BASE}/addons/${id}`),
  create: (addonData) => api.post(`${BASE}/addons`, addonData),
  update: (id, addonData) => api.put(`${BASE}/addons/${id}`, addonData),
  delete: (id) => api.delete(`${BASE}/addons/${id}`),
};

// --- Reports ---
export const membershipReportAPI = {
  getDashboardStats: (params) => api.get(`${BASE}/reports/dashboard`, { params }),
  getMemberReport: (params) => api.get(`${BASE}/reports/members`, { params }),
  getMembersMasterList: (params) => api.get(`${BASE}/reports/members-master`, { params }),
  getFinancialReport: (params) => api.get(`${BASE}/reports/financial`, { params }),
  getRetentionReport: (params) => api.get(`${BASE}/reports/retention`, { params }),
  getCpdReport: (params) => api.get(`${BASE}/reports/cpd`, { params }),
  getPractisingMarketerReport: (params) => api.get(`${BASE}/reports/practising-marketers`, { params }),
  exportReport: (type, params) => api.get(`${BASE}/reports/export/${type}`, { params, responseType: 'blob' }),
  getAuditLogs: (params) => api.get(`${BASE}/reports/audit-logs`, { params }),
  exportAuditLogs: (payload) => api.post(`${BASE}/reports/audit-logs/export`, payload, { responseType: 'blob' }),
};

// --- Settings ---
export const membershipSettingsAPI = {
  getAll: () => api.get(`${BASE}/settings`),
  update: (settings) => api.put(`${BASE}/settings`, settings),
  getEmailTemplates: () => api.get(`${BASE}/settings/email-templates`),
  updateEmailTemplate: (id, template) => api.put(`${BASE}/settings/email-templates/${id}`, template),
  getGeneratedLinks: () => api.get(`${BASE}/settings/generated-links`),
  updateGeneratedLinks: (values) => api.put(`${BASE}/settings/generated-links`, { values }),
  getPreprintedReceipt: () => api.get(`${BASE}/settings/preprinted-receipt`),
  updatePreprintedReceipt: (value) => api.put(`${BASE}/settings/preprinted-receipt`, { value }),
  getBankAccounts: () => api.get(`${BASE}/settings/bank-accounts`),
  addBankAccount: (data) => api.post(`${BASE}/settings/bank-accounts`, data),
  updateBankAccount: (id, data) => api.put(`${BASE}/settings/bank-accounts/${id}`, data),
  deleteBankAccount: (id) => api.delete(`${BASE}/settings/bank-accounts/${id}`),
  getApprovalNotifications: () => api.get(`${BASE}/settings/approval-notifications`),
  updateApprovalNotifications: (data) => api.put(`${BASE}/settings/approval-notifications`, data),
  getPmPaymentNotifications: () => api.get(`${BASE}/settings/pm-payment-notifications`),
  updatePmPaymentNotifications: (data) => api.put(`${BASE}/settings/pm-payment-notifications`, data),
  getNotifications: () => api.get(`${BASE}/settings/notifications`),
  updateNotification: (key, data) => api.put(`${BASE}/settings/notifications/${key}`, data),
  getScoreCardTemplates: () => api.get(`${BASE}/settings/pm-scorecard-templates`),
  createScoreCardTemplate: (data) => api.post(`${BASE}/settings/pm-scorecard-templates`, data),
  updateScoreCardTemplate: (id, data) => api.put(`${BASE}/settings/pm-scorecard-templates/${id}`, data),
  deleteScoreCardTemplate: (id) => api.delete(`${BASE}/settings/pm-scorecard-templates/${id}`),
  activateScoreCardTemplate: (id) => api.post(`${BASE}/settings/pm-scorecard-templates/${id}/activate`),
  getFinanceSettings: () => api.get(`${BASE}/settings/finance`),
  updateFinanceSettings: (data) => api.put(`${BASE}/settings/finance`, data),
  getFinanceOptions: () => api.get(`${BASE}/settings/finance/options`),
  getFinanceStatus: () => api.get(`${BASE}/settings/finance/status`),
  resyncFinance: () => api.post(`${BASE}/settings/finance/resync`),
  getMembershipCycle: () => api.get(`${BASE}/settings/membership-cycle`),
  updateMembershipCycle: (data) => api.put(`${BASE}/settings/membership-cycle`, data),
  runMembershipCycleNow: () => api.post(`${BASE}/settings/membership-cycle/run-now`),
};

// --- Member Removal Requests ---
export const memberRemovalAPI = {
  requestRemoval: (memberId, formData) => api.post(`${BASE}/members/${memberId}/request-removal`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getPendingRequests: () => api.get(`${BASE}/removal-requests`),
  approveRequest: (requestId, data) => api.post(`${BASE}/removal-requests/${requestId}/approve`, data),
  rejectRequest: (requestId, data) => api.post(`${BASE}/removal-requests/${requestId}/reject`, data),
};

export default membershipApi;
